import {
  type CartEventPayloadType,
  CartEvents,
  CartEventsEnum,
  CartEventsVersionsEnum,
  CartRoutePaths,
  redeemAndAddToCart,
} from '@commerce-cart-webcomponents/sdk';
import { ModalWrapper } from '@commerce-cart-webcomponents/ui';
import { logObject, ProductData, Slot } from '@commerce-product-webcomponents/sdk';
import {
  AlertMessage,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  Drawer,
  IconAlignment,
  IconStyle,
  Theme,
  useNextgenRouter,
  useTranslation,
} from '@nextgen-web-framework/all';
import { Logger } from '@nextgen-web-framework/core';
import { FC, useEffect, useState } from 'react';
import { APointRedeemProductCard } from '../../Organisms';
import { BundleSlotEntriesType, BundleSlotType } from '../../SearchCard/SearchCard';
import styles from './APointRedeemDrawer.module.scss';
import type { APointRedeemDrawerPropsTypes } from './types/propsTypes';

const loggerInstance = Logger.getInstance(logObject('APointRedeemDrawer'));

const APointRedeemDrawer: FC<APointRedeemDrawerPropsTypes> = (props: APointRedeemDrawerPropsTypes): JSX.Element => {
  const { onClose, aPointPriceCompanionText, showLink, productData, productQuantity } = props;

  const [closeDrawer, setCloseDrawer] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showAPointRedeemedModal, setShowAPointRedeemedModal] = useState(false);

  const t = useTranslation();
  const router = useNextgenRouter();

  const aPointRedeemedModalProps = productData.productFlags?.restrictFromPdpDisplay
    ? {
        primaryButtonLabel: t('product.APointRedeemDrawer.apointRedeemSuccessModal.viewCoupon'),
        onPrimarySuccess: () => {
          router.push(CartRoutePaths.CART);
        },
      }
    : {
        primaryButtonLabel: t('product.APointRedeemDrawer.apointRedeemSuccessModal.proceedToCart'),
        onPrimarySuccess: () => {
          router.push('/coupons');
        },
      };

  const onCloseBtnClick = (e: React.KeyboardEvent | React.MouseEvent | React.TouchEvent) => {
    if (onClose && typeof onClose === 'function') {
      onClose(e);
    }
    setCloseDrawer(true);
  };

  useEffect(() => {
    const redeemFailEvent = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.RedeemFailed],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: (cartEvent: string, { message }: CartEventPayloadType) => {
        loggerInstance.info('cartEvent', cartEvent);
        if (message) {
          setErrorMsg(message);
        }
      },
    });

    const redeemSuccessEvent = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.RedeemSuccessful],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: (cartEvent: string) => {
        loggerInstance.info('cartEvent', cartEvent);
        setErrorMsg('');
        setShowAPointRedeemedModal(true);
      },
    });

    return () => {
      CartEvents.unsubscribe([redeemFailEvent, redeemSuccessEvent]);
    };
  }, []);

  const confirmRedeemBtnClickHandler = () => {
    const { productId, bundle } = productData;
    let bundleSlots: BundleSlotType[] | undefined;
    if (bundle) {
      bundleSlots = bundle.slots?.map((slot: Slot) => {
        const bundleSlot: BundleSlotType = {
          id: slot.id?.toString() as string,
          entries: slot.products?.map((product: ProductData) => {
            const bundleSlotEntry = {
              product: {
                id: product.items[0].itemNumber as string,
              },
              quantity: slot.minQty,
            };
            return bundleSlotEntry;
          }) as unknown as BundleSlotEntriesType[],
        };
        return bundleSlot;
      }) as BundleSlotType[];
    }
    redeemAndAddToCart(productId, productQuantity ?? 1, bundleSlots);
  };

  return (
    <div className={styles['apoint-redeem-drawer-container']}>
      {!closeDrawer && (
        <Drawer
          header={<h5 className={styles['apoint-redeem-drawer-heading']}>{t('product.APointRedeemDrawer.heading')}</h5>}
          closeOnBackdropClick={true}
          closeIcon={true}
          onClose={onCloseBtnClick}
          bottomGap={0}
          footer={
            <div className={styles['apoint-redeem-drawer-footer']}>
              <Button
                id="confirm-redeem-apoint"
                title={t('product.APointRedeemDrawer.confirmToRedeem')}
                buttonStyle={ButtonStyles.SOLID}
                theme={Theme.BLACK}
                size={ButtonSize.SMALL}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                tabIndex={0}
                disabled={Boolean(errorMsg)}
                data-testid="confirm-redeem-apoint-btn"
                onClick={confirmRedeemBtnClickHandler}
              />
            </div>
          }
        >
          <div className={styles['apoint-redeem-drawer-body']} data-testid="apoint-redeem-drawer-body-testid">
            {errorMsg && (
              <div className={styles['apoint-redeem-error-msg-text']}>
                <AlertMessage
                  theme={Theme.ERROR}
                  hasIcon
                  icon="square-exclamation"
                  iconColor={Theme.ERROR}
                  iconStyle={IconStyle.REGULAR}
                  iconAlignment={IconAlignment.CENTER}
                  textColor={Theme.ERROR}
                >
                  {t(errorMsg)}
                </AlertMessage>
              </div>
            )}
            <div className={styles['apoint-redeem-instruction-text']}>
              {t('product.APointRedeemDrawer.instructionText')}
            </div>
            <div className={styles['apoint-redeem-product-card-container']}>
              <APointRedeemProductCard
                aPointPriceCompanionText={aPointPriceCompanionText}
                showLink={showLink}
                productData={productData}
              />
            </div>
          </div>
        </Drawer>
      )}
      {showAPointRedeemedModal && (
        <ModalWrapper
          headLineText={t('product.APointRedeemDrawer.apointRedeemSuccessModal.headingText')}
          description={t('product.APointRedeemDrawer.apointRedeemSuccessModal.descriptionText')}
          onCloseClick={() => setShowAPointRedeemedModal(false)}
          {...aPointRedeemedModalProps}
        />
      )}
    </div>
  );
};

export default APointRedeemDrawer;
export type { APointRedeemDrawerPropsTypes };
